import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import axiosInstance from 'utils/axiosUtil';
import fileDownload from 'js-file-download';
import { format } from 'date-fns';
import { StoreUtil } from 'doctivity-shared/utils';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { DynamicTable, LoadingView, CSVExportDialog } from 'components';
import { CsvExportUtil } from 'doctivity-shared/utils';
import { getColumns, availableDownloads, getCSV, prepareQuery } from './downloadTypes';
import { queryTaxonomyCodes } from 'store/actions/taxonomyActions';

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  header: {
    padding: theme.spacing(3),
    paddingBottom: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  headerTitle: {
    color: '#FFFFFF',
    fontSize: 24,
    paddingRight: 30,
  },
  tabs: {
    paddingTop: 3,
    paddingLeft: theme.spacing(3),
    backgroundColor: theme.palette.primary.accent,
  },
  indicator: {
    backgroundColor: '#FFFFFF',
    height: 2,
    marginBottom: 8,
  },
  headerDivider: {},
  tabPanel: {
    color: '#E0E0E0',
    '&.Mui-selected': {
      color: '#FFFFFF',
    },
    '&:hover': {
      color: '#FFFFFF',
    },
  },
  element: {
    paddingBottom: theme.spacing(2),
  },
  downloadForm: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(1),
  },
  displayedRowsBold: {
    fontWeight: '600',
  }
});

function debounce(func, delay = 250) {
    let debounceTimer;
    return function() {
        const context = this;
        const args = arguments;
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => func.apply(context, args), delay);
    }
}

class DataDownload extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedDownload: 0,
      showLoading: false,
      csvDialogOpen: false,
      query: {
        ...availableDownloads[0].initialQuery,
        limit: 25,
        offset: 0,
      },
      data: {loading: false},
    };

    // Monotonic id for the in-flight table fetch. fetchData is debounced and its response can
    // land after the user has moved on (switched download type, changed client, edited a
    // filter), so every response checks that it is still the newest request before it renders.
    // Without this, a slow Provider Mailing Addresses fetch resolves into the Contacts view.
    this.requestId = 0;
  }

  componentDidMount() {
    this.fetchData();
    
    sessionStorage.removeItem('selectedSpecialties');
    let values = [];

    if (values && values.length > 0) {
      this.fetchSpecialties(values);
    } else {
      this.taxonomyCodes = [];
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.selectedDownload !== this.state.selectedDownload) {
      this.setState(
        {
          query: {
            ...availableDownloads[this.state.selectedDownload].initialQuery,
            limit: 25,
            offset: 0,
          },
        },
        () => {
          this.fetchData();
        },
      );
    }
    if (prevState.query !== this.state.query) {
      this.fetchData();
    }
    // Client-scoped downloads (Contacts, and the provider email column) are filtered by the
    // selected client via applyStore, so refetch the visible table when the client switches.
    if (prevProps.app?.selectedClient !== this.props.app?.selectedClient) {
      this.fetchData();
    }
  }

  fetchSpecialties(values) {
    const { dispatch } = this.props;

    let queryCodes = {
      where: { code: values},
      order: [['code', 'ASC']],
      limit: 10000,
      offset: 0,
    }

    let qryCodes = queryTaxonomyCodes({
      ...queryCodes,
    });

    dispatch(
      qryCodes
    );
  }
  

  render() {
    const { showLoading, query, selectedDownload, data, csvDialogOpen } = this.state;
    const { classes, taxonomyCodes } = this.props;
    const columns = getColumns(selectedDownload);
    const download = availableDownloads[selectedDownload];
    const FilterForm = download.filterForm;
    const rowCap = CsvExportUtil.getRowCap(download.endpoint);
    if (showLoading) {
      return <LoadingView />;
    }

    return (
      <>
        <title>Data Download</title>
        <div className={classes.container}>
          <FormControl variant='outlined' className={classes.element}>
            <InputLabel>Download Type</InputLabel>
            <Select
              value={selectedDownload}
              label='Download Type'
              onChange={this.onDownloadTypeChange}
            >
              {availableDownloads.map((d, idx) => (
                <MenuItem key={idx} value={idx}>
                  {d.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DynamicTable
            header={
              <div className={classes.headerContainer}>
                <Typography
                  component='h2'
                  variant='h6'
                  color='primary'
                  gutterBottom
                >
                  Download
                </Typography>
                <p>{download.helpText}</p>
                <p>
                  <h6>{`NOTE: UP TO ${rowCap.toLocaleString()} ROWS PER DOWNLOAD — CHOOSE A START ROW TO GET THE REST`}</h6>
                </p>
                <div className={classes.downloadForm}>
                  {FilterForm && (
                    <FilterForm query={query} setQuery={this.setQuery} />
                  )}
                  <Button
                    variant='contained'
                    onClick={() => this.setState({ csvDialogOpen: true })}
                    color='primary'
                  >
                    Download
                  </Button>
                </div>
                <div className={classes.tableHeader} />
              </div>
            }
            classes={data?.count > rowCap ? { displayedRows: classes.displayedRowsBold } : {}}
            columns={columns}
            query={query}
            taxonomyCodes={taxonomyCodes}
            onQueryChange={this.onQueryChange}
            data={data}
          />
        </div>
        <CSVExportDialog
          open={csvDialogOpen}
          onClose={() => this.setState({ csvDialogOpen: false })}
          onConfirm={this.fetchCSVDownload}
          title={`Download ${download.displayName}`}
          exportType={download.endpoint}
          totalCount={data?.count}
          defaultRows={1000}
          defaultFileName={this.getFilename()}
        />
      </>
    );
  }

  setQuery = (query) => {
    this.setState({ query });
  };

  fetchData = debounce(async () => {
    // No `if (loading) return` guard here: it dropped the NEW request and let the OLD one
    // render, so switching download type mid-load showed the previous report's rows under the
    // new selection. The newest request always wins instead; stale responses are discarded.
    const requestId = ++this.requestId;
    this.setState({ data: { loading: true } },async () => {
      const { query, selectedDownload } = this.state;
      const downloadInfo = availableDownloads[selectedDownload];
      // Inject any store-derived associations (e.g. the client-scoped email in
      // related_clients) and promote filtered includes, so the on-screen table shows the same
      // columns AND applies the same filters as the CSV. prepareQuery always returns a copy,
      // so this does not mutate component state.
      const tableQuery = prepareQuery(downloadInfo, query, this.props.store);
      try {
        const results = await axiosInstance.get(`/${downloadInfo.endpoint}`, {
          params: {
            opts: tableQuery,
          },
        });
        // Superseded by a newer fetch — drop this response rather than render it into
        // whatever the user is now looking at.
        if (requestId !== this.requestId) return;
        this.setState({ data: { ...results.data, loading: false }});
      } catch (err) {
        // Must clear `loading` on failure. /Contacts legitimately 403s while the store
        // rehydrates a `?client=` param, and leaving loading set would strand the view.
        console.error(`Could not load ${downloadInfo.endpoint} data`, err);
        if (requestId !== this.requestId) return;
        this.setState({ data: { rows: [], count: 0, loading: false } });
      }
    });
  });
  
  getFilename = () => {
    const { selectedDownload } = this.state;
    const downloadInfo = availableDownloads[selectedDownload];
    return downloadInfo.downloadName.replace('{date}' ,format(new Date(), 'yy_MM_dd'));
  }

  fetchCSVDownload = async ({ limit, offset, fileName } = {}) => {
    const { query, selectedDownload } = this.state;
    this.setState({ csvDialogOpen: false });
    try {
      const data = await getCSV(
        availableDownloads[selectedDownload],
        query,
        this.props.store,
        { limit, offset },
      );
      const filename = fileName || this.getFilename();
      try {
        fileDownload(data, filename);
      } catch (err) {
        console.error(`Could not format csv for ${filename}`);
        console.error(err);
      }
    } catch (e) {
      console.error(e);
    }
  };

  onQueryChange = (query) => {
    if (query.where && Object.keys(query.where).length === 0) {
      delete query.where;
    }
    //this.setState({ query, showLoading: false });
    this.setState({ query, showLoading: false }, this.fetchData.bind(this));
  };

  onDownloadTypeChange = (event) => {
    this.setState({ selectedDownload: event.target.value });
  };
}

DataDownload.propTypes = {
  classes: PropTypes.object.isRequired,
  app: PropTypes.object,
  user: PropTypes.object,
  dispatch: PropTypes.func,
  store: PropTypes.object,
  taxonomyCodes: PropTypes.object.isRequired,  
};

function mapStateToProps(state) {
  const { app, user, taxonomyCodes } = state;

  return {
    app,
    user,
    store: state,
    taxonomyCodes: StoreUtil.get(taxonomyCodes, StoreUtil.COMMON_TABLE)
  };
}

const styled = withStyles(styles)(DataDownload);
const connected = connect(mapStateToProps)(styled);
export { connected as DataDownload };
